import axios from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/functions";
import "firebase/storage";
import {ApiProvider} from "@evennt-tech/api-v2-external";
import {
  observabilityAction,
  observabilityError,
  observabilityIdentify,
} from "tools/observability";
import {notify} from "tools/snackbar";
import {useNetworkStatus} from "store/network-status/network-status.store";
import {Roles} from "@evennt-tech/api-v2-core";
import Auth from "ts/api/auth";
import {
  logoutUser,
  registerAuthTransportReset,
  registerBackendLogout,
} from "tools/auth/logoutCleanup";
import {
  clearSessionSwitchInProgress,
  emitAuthSessionEvent,
  isNoticeRedirectInFlight,
  isSessionSwitchInProgress,
} from "tools/auth/sessionSync";
import {flagTwoFactorPromptOnLogin} from "tools/auth/twoFactorLoginPrompt";
import {isPublicRoute} from "tools/routing";
import {isExpectedEmbeddedShowsRejection} from "tools/error";
import {getRegisteredStore} from "store/store-registry";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN,
  // databaseURL: import.meta.env.VITE_DATABASE_URL,
  projectId: import.meta.env.VITE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID,
};

const app = firebase.initializeApp(firebaseConfig);

export const db = app.firestore();

db.settings({
  ignoreUndefinedProperties: true,
  experimentalAutoDetectLongPolling: true,
});

const RECONNECT_COOLDOWN_MS = 5_000;
let lastReconnectAt = 0;
const reconnectFirestore = async (): Promise<void> => {
  const now = Date.now();
  if (now - lastReconnectAt < RECONNECT_COOLDOWN_MS) return;
  lastReconnectAt = now;
  try {
    await db.disableNetwork();
    await db.enableNetwork();
  } catch {
    // SDK is mid-transition or already shutting down — the next lifecycle
    // event will retry. Errors here are not actionable.
  }
};

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    reconnectFirestore();
  }
});

window.addEventListener("online", () => {
  reconnectFirestore();
});

const functions = app.functions();
export const auth = app.auth();

// Firebase restores auth state from IndexedDB asynchronously after the SDK
// loads. Requests fired before `auth.currentUser` is populated used to go out
// without an `access-token` header, triggering 401 → `onUnauthorized` →
// `logoutUser`. On fast networks the 401 beat the SDK init and the user was
// kicked to /login; on slow networks the SDK caught up in time and the retry
// attached a fresh token (race).
//
// `awaitAuthReady` resolves as soon as the SDK fires its first auth state
// change (with either a user or null), ensuring every subsequent request has
// a truthful view of `auth.currentUser`. A 3-second timeout prevents a hung
// SDK from wedging the whole app.
const AUTH_READY_TIMEOUT_MS = 3000;
let authReadyPromise: Promise<void> | null = null;
const awaitAuthReady = (): Promise<void> => {
  if (!authReadyPromise) {
    authReadyPromise = new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        unsubscribe();
        resolve();
      };
      const unsubscribe = auth.onAuthStateChanged(finish);
      setTimeout(finish, AUTH_READY_TIMEOUT_MS);
    });
  }
  return authReadyPromise;
};

export const apiV2: ApiProvider = new ApiProvider({
  notification: {
    url: import.meta.env.VITE_NOTIFICATION_SERVICE_URL as string,
    path: import.meta.env.VITE_NOTIFICATION_SERVICE_PATH as string,
    getToken: async () => {
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }

      return "";
    },
  },

  fileUpload: {
    wb: import.meta.env.VITE_STORAGE_SERVICE_WS_URL as string,
    wbPath: import.meta.env.VITE_STORAGE_SERVICE_WS_PATH as string,

    http: import.meta.env.VITE_STORAGE_SERVICE_HTTP_URL as string,
    getToken: async () => {
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }

      return "";
    },
  },

  config: {
    withCredentials: true,
    baseURL: import.meta.env.VITE_API_V2_URL,
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
  },

  onUnauthorized: async () => {
    // We are mid-redirect to /session-changed because another tab just
    // changed the session. An in-flight query that started before the
    // redirect is now coming back as 401 (it raced the new session
    // cookie). Running `logoutUser` here would call
    // `clearClientAuthState` → `localStorage.clear()` +
    // `clearFrontendCookies()`, both of which destroy storage SHARED
    // with the originating tab — wiping the freshly-set session
    // cookie and turning one cross-tab event into a full cascade
    // logout. The redirect handles teardown via natural reload.
    if (isNoticeRedirectInFlight()) {
      return;
    }

    // Anonymous / public pages (login, sign-up, email review, the anonymous
    // grid preview, …) legitimately render shared components that fire
    // authenticated-only requests. Those 401 for a logged-out viewer — that's
    // expected, NOT a reason to redirect to /login (which would break the
    // anonymous page). The page itself only depends on public endpoints.
    if (isPublicRoute()) {
      return;
    }

    // Skip the backend `POST /auth/logout` and cross-tab broadcast here.
    //
    // `onUnauthorized` fires when an axios request finishes with a 401 that
    // couldn't be retried. But by the time we reach this handler the current
    // cookies may belong to a DIFFERENT user than the request thought — most
    // commonly right after impersonation in another tab. Calling backend
    // logout would then destroy that other user's legitimate session; the
    // broadcast would then cascade the same bogus logout to every tab.
    //
    // Local cleanup (Firebase signOut, cache clear, redirect to /login) is
    // always safe. Firebase's IndexedDB sync is what keeps other tabs in
    // step, not our localStorage broadcast.
    await logoutUser({
      reason: "unauthorized",
      skipBackendLogout: true,
      broadcast: false,
    });
  },

  onRejected: async (error: any) => {
    // Track network-level failures and server errors (5xx)
    if (!error?.response || error?.response?.status >= 500) {
      useNetworkStatus.getState().reportFailedRequest();
    }

    // Response to a request issued by a DIFFERENT identity than the one
    // signed in now — a query fired as organiser/admin that lands (or is
    // retried) after an impersonation hand-off or a logout. Its verdict says
    // nothing about the current session: an organiser-scoped endpoint called
    // with the impersonated exhibitor's token answers 403, which used to
    // reach the user as an "Access denied" snackbar and OpenReplay as a
    // real error. Drop it here, before ANY of that — this guard deliberately
    // covers every status, not just 401 (which is all it used to cover, and
    // exactly why the 403s got through).
    const staleRequestUid = error?.config?.__authUid as string | undefined;
    if (staleRequestUid && auth.currentUser?.uid !== staleRequestUid) {
      if (error.config) {
        // Skip the ApiProvider's `onUnauthorized` cascade too.
        error.config._onUnauthorizedFired = true;
      }
      return Promise.reject(error);
    }

    if (error?.config) {
      const originalRequest = error?.config;

      if (error.response?.status === 401 && !originalRequest?.sent) {
        originalRequest.sent = true;

        // Only wait for Firebase init if we don't already have a current user.
        // Otherwise (e.g. right after impersonation's `signInWithCustomToken`)
        // we already know the user and should retry immediately.
        if (!auth.currentUser) {
          await awaitAuthReady();
        }

        if (auth.currentUser) {
          // Try a forced refresh first; if Firebase has a transient hiccup
          // (network flap, backend throttling) fall back to the cached ID
          // token so a single 401 does not escalate into a logout.
          let freshToken: string | null = null;
          try {
            freshToken = await auth.currentUser.getIdToken(true);
          } catch (refreshError) {
            console.warn(
              "Firebase token force-refresh failed, falling back to cached token",
              refreshError
            );
            try {
              freshToken = await auth.currentUser.getIdToken(false);
            } catch (cachedError) {
              console.warn("Failed to read cached Firebase token", cachedError);
            }
          }

          if (freshToken) {
            originalRequest.headers["access-token"] = freshToken;
            return apiV2.axiosInterceptors(originalRequest);
          }
        }
      }
    }

    // The public embedded widget answers 403/404 for a disabled list or an
    // unlisted show and 429 when rate limited. The widget shows its own copy
    // for those ("This event is no longer online"); they are neither an access
    // problem worth a snackbar nor an API fault worth reporting.
    if (isExpectedEmbeddedShowsRejection(error)) {
      return Promise.reject(error);
    }

    // A session switch is in flight (login, impersonation hand-off, return
    // from impersonation): requests started under the outgoing identity are
    // still draining, and a 403 from one of them is an artefact of the
    // switch, not something the user did. The `__authUid` guard above catches
    // most of them; this covers the window where Firebase has already dropped
    // `currentUser` (so there is no uid to compare against) but the new
    // session is not established yet.
    if (
      error?.response?.status === 403 &&
      !isSessionSwitchInProgress() &&
      !isNoticeRedirectInFlight() &&
      import.meta.env.VITE_APP_VERSION !== "prod"
    ) {
      notify("Access denied", {variant: "error"});
    }

    // Report meaningful API failures to Datadog + OpenReplay: backend 4xx/5xx
    // AND requests that never got a response at all (backend down, CORS,
    // DNS, timeout — a real outage, previously invisible because there's no
    // status code for it). Skip transient 401s (handled by the retry above)
    // and deliberate cancellations (component unmount / navigation aborting
    // an in-flight request via axios's own cancel API — not a failure).
    // `observabilityError` maps a no-response error to `status: 0` so
    // `shouldDrop`'s existing noise rules get a chance to filter it same as
    // any other reported error.
    const status = error?.response?.status as number | undefined;
    const shouldReport =
      !axios.isCancel(error) &&
      (status === undefined || (status !== 401 && status >= 400));
    if (shouldReport) {
      observabilityError(error, {source: "apiV2"});
    }

    return Promise.reject(error);
  },

  onRequest: async (config) => {
    // Only hold the request when Firebase still hasn't populated a current
    // user. Otherwise (active session, just-completed login / impersonation)
    // we already have the user — attach the token and proceed immediately.
    // Waiting unconditionally once regressed impersonation: by the time the
    // wait resolved, the UI had already navigated and stale queries raced
    // the new session.
    if (!auth.currentUser) {
      await awaitAuthReady();
    }

    if (auth.currentUser) {
      config.headers["access-token"] = await auth.currentUser.getIdToken();
      // Stamp the request with the current auth UID so that if a later 401
      // response comes back after we've switched identity (e.g. admin →
      // impersonated), `onRejected` can tell this was a stale request and
      // skip the retry / `onUnauthorized` cascade.
      (config as {__authUid?: string}).__authUid = auth.currentUser.uid;
    }

    return config;
  },
});

registerAuthTransportReset(() => {
  apiV2.notification.reset();
});

// Use the auth-instrumented gateway method so the `access-token`
// header reaches the backend. The cookies (`_session_id`,
// `_persistent_login`) are HttpOnly and can ONLY be cleared by the
// server, so this call is mandatory for a clean logout. The gateway
// swallows network errors internally; the timeout in
// `logoutCleanup.requestBackendLogout` still applies via Promise.race.
registerBackendLogout(() => apiV2.auth.logOut());

export const {FieldValue, Timestamp} = firebase.firestore;

export type WhereFilterOp = firebase.firestore.WhereFilterOp;
export type FieldPath = firebase.firestore.FieldPath;
export type DocumentReference<T> = firebase.firestore.DocumentReference<T>;
export type FirebaseError = firebase.FirebaseError;
export type CollectionReference<T> = firebase.firestore.CollectionReference<T>;
export type Query<T> = firebase.firestore.Query<T>;
export type DocumentSnapshot<T> = firebase.firestore.DocumentSnapshot<T>;
export type QuerySnapshot<T> = firebase.firestore.QuerySnapshot<T>;
export type Snapshot<T> = firebase.firestore.DocumentSnapshot<T>;
export type QueryDocumentSnapshot<
  T
> = firebase.firestore.QueryDocumentSnapshot<T>;
export type DocumentData = firebase.firestore.DocumentData;
export type Timestamp = firebase.firestore.Timestamp;
export type Transaction = firebase.firestore.Transaction;
export type FieldValueType = firebase.firestore.FieldValue;
export type WithFieldValue<T> = {[K in keyof T]: T[K] | FieldValueType};
export type WriteBatch = firebase.firestore.WriteBatch;

export type UploadTask = firebase.storage.UploadTask;

export const dateToTimestamp = (date: Date) =>
  firebase.firestore.Timestamp.fromDate(date);

export const timestampToDate = (
  seconds: number,
  nanoseconds: number | undefined = 0
) => new firebase.firestore.Timestamp(seconds, nanoseconds || 0).toDate();

// TODO: Update firebase lib to use storage emulator locally
export default firebase;
export const storage = firebase.storage();

export const BASE_STORAGE_PATH = `https://firebasestorage.googleapis.com/v0/b/${
  import.meta.env.VITE_STORAGE_BUCKET
}/o`;
// export const BASE_STORAGE_PATH = `http://localhost:9199/v0/b/${import.meta.env.VITE_STORAGE_BUCKET}/o`;

if (
  import.meta.env.MODE === "development" &&
  !import.meta.env.VITE_USE_SANDBOX &&
  !import.meta.env.VITE_DONT_USE_EMULATORS
) {
  db.useEmulator("127.0.0.1", 8080);
  storage.useEmulator("localhost", 9199);
  functions.useEmulator("127.0.0.1", 5001);
  auth.useEmulator("http://127.0.0.1:9099");
}

export const selectUser = (): Auth | null => {
  const {auth} = getRegisteredStore().getState();
  return auth.id ? auth : null;
};

export const getFileFromUrl = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsB-getFileFromUrl");

export const getCustomerId = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsB-getCustomerId");

export const makePayment = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsB-makePayment");

export const sendCancelOrderEmail = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsC-sendCancelOrderEmail");

export const sendRequestPriceEmail = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsC-sendRequestPriceEmail");
export const reconcileCardOrder = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsC-reconcileCardOrder");

export const deleteAbandonedCardOrders = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsC-deleteAbandonedCardOrders");

export const loadDataToGoogleDrive = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsB-loadDataToGoogleDrive");

export const updateOrganisersSyncedShows = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsB-updateOrganisersSyncedShows");

export const createSession = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsD-createSession");

export const getSessionStatus = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("endpointsD-getSessionStatus");

export const getFileDownloadURL = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("public-getFileDownloadURL");

export const setCustomReportFeed = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("public-setCustomReportFeed");

export const setCustomReportToCache = (): firebase.functions.HttpsCallable =>
  firebase
    .functions()
    .httpsCallable("endpointsE-setCustomReportToCache", {timeout: 180_000});

export const testZapierConnection = (): firebase.functions.HttpsCallable =>
  firebase.functions().httpsCallable("public-testZapierConnection");

export const signInWithCustomToken = async (token: string): Promise<void> => {
  flagTwoFactorPromptOnLogin();
  try {
    const user = await firebase.auth().signInWithCustomToken(token);
    clearSessionSwitchInProgress();
    observabilityIdentify(user.user);
    await apiV2.user.getMe();
    await apiV2.notification.connect();
    emitAuthSessionEvent("login");
    observabilityAction("login.success");
  } catch (error) {
    observabilityAction("login.failure", {
      reason: (error as {code?: string})?.code ?? "unknown",
    });
    throw error;
  }
};

export const onAuthStateChanged = (
  callback: (a: firebase.User | null) => any
): firebase.Unsubscribe => {
  return firebase.auth().onAuthStateChanged(callback);
};

export const checkIfUserExists = async (email: string) => {
  try {
    const {exist} = await apiV2.auth.checkIfUserExists({
      email,
    });

    return exist;
  } catch (e) {
    console.error(e);
  }
};

export const checkIfEligibleForPasswordReset = async (email: string) => {
  try {
    const {eligible} = await apiV2.auth.checkIfEligibleForPasswordReset({
      email,
    });

    return eligible;
  } catch (e) {
    console.error(e);
  }
};

export const activateUser = async (showId: string) => {
  const user = selectUser();
  if (user) {
    const {id} = user;

    const {role} = await apiV2.user.getMe();

    if (role !== Roles.EXHIBITOR) {
      await db
        .collection("shows")
        .doc(showId)
        .collection(`${role}s`)
        .doc(id)
        .update({status: "active"});
    }
  }
};
